import { z } from 'zod';

import { RmDirectoryIdentifierSchema, RmReviewRatingFilterSchema } from '@brightlocal/shared';

import { REVIEW_PERIOD } from 'shared/modules/reviews';

import { REVIEW_SORT, REVIEW_TAB } from './consts';

/**
 * A list param that keeps the items `itemSchema` accepts and drops the rest; a value that is no list is left out. An
 * item is kept as it came, so `itemSchema` can't transform: its input and output types have to be the same.
 */
const listParam = <Item>(itemSchema: z.ZodType<Item, z.ZodTypeDef, Item>) => {
  const isValidItem = (item: unknown): item is Item => itemSchema.safeParse(item).success;

  return z
    .array(z.unknown())
    .transform((items) => items.filter(isValidItem))
    .optional()
    .catch(undefined);
};

/** Search params of the Review Manager route. */
export const reviewManagerSearchSchema = z.object({
  /** The open status tab; absent, or any other value, opens Needs action. */
  tab: z.nativeEnum(REVIEW_TAB).optional().catch(undefined),
  /** Text the reviews are searched for; absent searches every review. */
  search: z.string().optional().catch(undefined),
  /** Review sites the reviews come from; absent means every site. */
  sites: listParam(RmDirectoryIdentifierSchema),
  /** Ratings the reviews have; absent means every rating. */
  ratings: listParam(RmReviewRatingFilterSchema),
  /** How far back the reviews were published; absent means All time. */
  period: z.nativeEnum(REVIEW_PERIOD).optional().catch(undefined),
  /** The order of the reviews; absent, or any other value, lists the newest first. */
  sort: z.nativeEnum(REVIEW_SORT).optional().catch(undefined)
});

/** Search params of the Review Manager route, as the route reads them. */
export type ReviewManagerSearch = z.infer<typeof reviewManagerSearchSchema>;
